import {
  Button,
  Divider,
  HStack,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  AdminEditDistributionGroupMutation,
  AdminEditDistributionGroupMutationVariables,
  UserFragment,
} from "gql/graphql";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import {
  InboxIcon,
  InboxSlashIcon,
  GroupsOutlineIcon,
  AdminAddToGroupIcon,
} from "modules/icons";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import DistributionGroupName from "modules/_shared/distribution-group-name";
import DistributionGroupTooltip from "modules/_shared/distribution-group-tooltip";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import { useMutation } from "@apollo/client";
import ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION from "../_networking/admin-edit-distribution-group";
import { sortByName } from "modules/utils/Functions";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";

interface IProps {
  user: UserFragment;
}

const AdminUserDistributionGroups = ({ user }: IProps) => {
  const { distributionGroups } = useDistributionGroups();
  const { openModal } = useOverlay();

  const distributionGroupList = useMemo(
    () =>
      distributionGroups
        .filter((obj) => obj.userIdList.includes(user.id))
        .sort(sortByName),
    [user.id, distributionGroups],
  );

  const [adminEditDistributionGroupMutationAPI] = useMutation<
    AdminEditDistributionGroupMutation,
    AdminEditDistributionGroupMutationVariables
  >(ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION);

  const onToggleNotificationsDistributionGroupClicked = useCallback(
    (distributionGroupId: string, enableNotifications: boolean) => () => {
      if (enableNotifications) {
        adminEditDistributionGroupMutationAPI({
          variables: {
            id: distributionGroupId,
            input: {
              notificationUserIdListToAdd: [user.id],
            },
          },
        });
        return;
      }
      adminEditDistributionGroupMutationAPI({
        variables: {
          id: distributionGroupId,
          input: {
            notificationUserIdListToRemove: [user.id],
          },
        },
      });
    },
    [adminEditDistributionGroupMutationAPI, user.id],
  );

  const onAddToGroupsClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.ADMIN_ADD_USERS_TO_DISTRIBUTION_GROUPS,
      ids: [user.id],
    });
  }, [openModal, user.id]);

  return (
    <VStack w="full" alignItems="flex-start">
      <HStack w="full">
        <GroupsOutlineIcon boxSize={6} fill={colors.blackSecondary} />
        <Text fontWeight={700} fontSize={18}>
          Groups ({distributionGroupList.length})
        </Text>
        <Spacer />
        <Button
          variant="hover"
          fontSize={14}
          fontWeight={600}
          size="xs"
          color={colors.orangePrimary}
          leftIcon={
            <AdminAddToGroupIcon boxSize={4} fill={colors.orangePrimary} />
          }
          onClick={onAddToGroupsClicked}
        >
          Add to Group(s)
        </Button>
      </HStack>
      <Divider borderColor={colors.black} />
      {distributionGroupList.length === 0 && (
        <Text color={colors.gray50} fontStyle="italic" fontSize={14}>
          No groups assigned yet
        </Text>
      )}
      {distributionGroupList.map((distributionGroup) => {
        const notifcationsEnabled =
          distributionGroup.notificationUserIdList.includes(user.id);
        return (
          <React.Fragment key={distributionGroup.id}>
            <HStack w="full">
              <DistributionGroupTooltip id={distributionGroup.id}>
                <Text
                  color={
                    distributionGroup.active ? colors.blue : colors.grayPrimary
                  }
                  fontSize={14}
                  fontWeight={700}
                >
                  <DistributionGroupName
                    distributionGroupId={distributionGroup.id}
                  />
                </Text>
              </DistributionGroupTooltip>
              <Spacer />
              <CircularIconButton
                aria-label={
                  notifcationsEnabled ? "notifications on" : "notifications off"
                }
                variant="ghost"
                onClick={onToggleNotificationsDistributionGroupClicked(
                  distributionGroup.id,
                  !notifcationsEnabled,
                )}
                icon={
                  notifcationsEnabled ? (
                    <SyncoTooltip label="Group messages sent to Threads Inbox">
                      <InboxIcon fill={colors.blue} boxSize={5} />
                    </SyncoTooltip>
                  ) : (
                    <SyncoTooltip label="Group messages not sent to Threads Inbox">
                      <InboxSlashIcon fill={colors.grayMain} boxSize={5} />
                    </SyncoTooltip>
                  )
                }
              />
            </HStack>
            <Divider />
          </React.Fragment>
        );
      })}
    </VStack>
  );
};

export default memo(AdminUserDistributionGroups);
